import {
  ApiError,
  LIBRARY_DOCUMENT_ACCEPT,
  LIBRARY_DOCUMENT_EXTENSIONS,
  LIBRARY_DOCUMENT_TYPES,
  type LibraryDocumentRow,
  MAX_LIBRARY_DOCUMENT_BYTES,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, FileText, Trash2, Upload } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { api, apiBaseUrl } from '../lib/api.js'
import { InfiniteScrollFooter } from './data-table.js'
import { useDebouncedValue } from './image-library-grid.js'

const PAGE_SIZE = 50

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const typeLabel = (contentType: string): string =>
  (LIBRARY_DOCUMENT_TYPES as Record<string, string>)[contentType] ?? 'File'

const fileUrl = (doc: LibraryDocumentRow, download = false) =>
  `${apiBaseUrl}/library-documents/${doc.id}/file${download ? '?download=1' : ''}`

/** The Documents tab of the Image library: upload, search, open, delete (t1389). */
export function LibraryDocuments({ canWrite }: { canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  const [deleting, setDeleting] = useState<LibraryDocumentRow | null>(null)
  const [uploading, setUploading] = useState(false)

  const filters = { q: q || undefined, pageSize: PAGE_SIZE }
  const query = useInfiniteQuery({
    queryKey: queryKeys.libraryDocuments.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: LibraryDocumentRow[]; total: number; page: number; pageSize: number }>(
        '/library-documents',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = query.data?.pages.flatMap((p) => p.items) ?? []
  const total = query.data?.pages[0]?.total ?? 0
  const { fetchNextPage } = query
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage])

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true }>(`/library-documents/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.libraryDocuments.all() })
      toast.success('Document deleted')
      setDeleting(null)
    },
    onError: (err) => {
      toast.error(
        'Could not delete the document',
        err instanceof ApiError ? err.message : undefined,
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryDocuments.all() })
      setDeleting(null)
    },
  })

  async function uploadFiles(files: File[]) {
    setUploading(true)
    let saved = 0
    for (const file of files) {
      const lower = file.name.toLowerCase()
      if (!(LIBRARY_DOCUMENT_EXTENSIONS as readonly string[]).some((ext) => lower.endsWith(ext))) {
        toast.error(`Could not upload ${file.name}`, 'Only PDF and Word files (.pdf, .doc, .docx)')
        continue
      }
      if (file.size > MAX_LIBRARY_DOCUMENT_BYTES) {
        toast.error(
          `Could not upload ${file.name}`,
          `Files must be ${MAX_LIBRARY_DOCUMENT_BYTES / 1024 / 1024} MB or smaller`,
        )
        continue
      }
      try {
        const formData = new FormData()
        formData.append('file', file)
        await api.postFile('/library-documents', formData)
        saved += 1
      } catch (err) {
        toast.error(
          `Could not upload ${file.name}`,
          err instanceof ApiError ? err.message : undefined,
        )
      }
    }
    setUploading(false)
    if (saved > 0) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.libraryDocuments.all() })
      toast.success(saved === 1 ? 'Document uploaded' : `${saved} documents uploaded`)
    }
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="min-w-48 max-w-sm flex-1">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by file name"
            aria-label="Search the documents"
          />
        </div>
        {canWrite && (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              accept={LIBRARY_DOCUMENT_ACCEPT}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                e.target.value = ''
                if (files.length > 0) void uploadFiles(files)
              }}
            />
            <Button loading={uploading} onClick={() => fileInput.current?.click()}>
              <Upload /> Upload documents
            </Button>
          </>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {query.isLoading ? (
          <p className="py-8 text-center text-sm text-foreground-muted">Loading documents…</p>
        ) : query.isError ? (
          <p className="py-8 text-center text-sm text-danger">Could not load the documents.</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={q ? 'No documents match that search' : 'No documents yet'}
            description={
              q
                ? 'Try part of the file name instead.'
                : 'Upload PDF or Word files to keep them here.'
            }
          />
        ) : (
          <div>
            <ul className="divide-y divide-border">
              {items.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 p-3">
                  <FileText className="size-5 shrink-0 text-foreground-muted" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <a
                      href={fileUrl(doc)}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate text-sm font-medium hover:underline"
                      title={doc.filename}
                    >
                      {doc.filename}
                    </a>
                    <p className="text-xs text-foreground-muted tabular">
                      {formatSize(doc.sizeBytes)} · {new Date(doc.createdAt).toLocaleDateString()}
                      {doc.uploadedBy ? ` · ${doc.uploadedBy.name}` : ''}
                    </p>
                  </div>
                  <Badge>{typeLabel(doc.contentType)}</Badge>
                  <Button variant="ghost" size="sm" asChild>
                    <a
                      href={fileUrl(doc, true)}
                      aria-label={`Download ${doc.filename}`}
                      title="Download"
                    >
                      <Download />
                    </a>
                  </Button>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${doc.filename}`}
                      title="Delete"
                      onClick={() => setDeleting(doc)}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            <InfiniteScrollFooter
              total={total}
              loaded={items.length}
              hasMore={!!query.hasNextPage}
              isFetchingMore={query.isFetchingNextPage}
              onLoadMore={loadMore}
            />
          </div>
        )}
      </div>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deleting?.filename ?? 'this document'}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              This removes the document from the library for good.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
